import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useUpdateContent } from '../hooks/useCustomContent';
import { TagsInput } from './TagsInput';
import type { AudioContentDto } from '../types';

interface EditContentModalProps {
  content: AudioContentDto;
  isOpen: boolean;
  onClose: () => void;
}

export function EditContentModal({ content, isOpen, onClose }: EditContentModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateContent(content.id);

  const [title, setTitle] = useState(content.title);
  const [tags, setTags] = useState<string[]>(content.tags);

  useEffect(() => {
    if (isOpen) {
      setTitle(content.title);
      setTags(content.tags);
    }
  }, [isOpen, content]);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    try {
      await update.mutateAsync({ title: title.trim(), tags });
      toast({ status: 'success', title: t('customContent.saved'), duration: 2000 });
      onClose();
    } catch (e) {
      toast({
        status: 'error',
        title: t('common.error', 'Something went wrong'),
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('customContent.editClip')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>{t('customContent.clipTitle')}</FormLabel>
              <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>{t('customContent.tags')}</FormLabel>
              <TagsInput value={tags} onChange={setTags} placeholder={t('customContent.addTag')} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button colorScheme="brand" onClick={handleSubmit} isLoading={update.isPending} isDisabled={!title.trim()}>
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EditContentModal;
